// The words a grid's sort chip shows (component/table-query-items.js): the
// sorted column's header and the direction as it reads for what the column
// holds -- "Sorted by Created, newest first", "Sorted by Email, A to Z",
// "Sorted by Count, highest first" -- rather than the raw field and ASC/DESC
// ("Sort By CreatedAt DESC").
//
// What a column holds comes from its type when the type says (ColumnDate and
// the other date columns, the duration and size columns: table-columns.js puts
// it on the column definition as context.sortKind), otherwise from the row
// values the grid was handed, otherwise from the column's fallback (a
// ColumnText is text; a "...At" field is a time). Any column can also say
// outright: <ColumnFormat sortKind="date" ...> (or "version": app versions,
// oldest or newest first). A column with its own
// comparator sorts by its own rule, so its words stay neutral: ascending /
// descending.

const DIRECTION_WORDS = {
  date: { asc: 'oldest first', desc: 'newest first' },
  number: { asc: 'lowest first', desc: 'highest first' },
  text: { asc: 'A to Z', desc: 'Z to A' },
  // app versions, in their own order (insights/versions.js)
  version: { asc: 'oldest first', desc: 'newest first' }
}

const NEUTRAL_WORDS = { asc: 'ascending', desc: 'descending' }

// The header AG Grid makes for a column with no headerName (its
// camelCaseToHumanText): "createdAt" -> "Created At".
export const humanizeField = field =>
  String(field || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z])([a-z])/g, '$1 $2$3')
    .replace(/\./g, ' ')
    .split(' ')
    .map(word => word.substring(0, 1).toUpperCase() + word.substring(1))
    .join(' ')

// AG Grid's column ids: colId, else field, made unique with _1, _2 ... in
// column order (its ColumnKeyCreator). The sort the query string carries is
// one of these.
const columnIDs = columns => {
  const taken = {}
  return columns.map(column => {
    const base = column.colId ?? column.field
    let id = base
    for (let count = 1; taken[id]; count++) id = `${base}_${count}`
    taken[id] = true
    return id
  })
}

export const findSortColumn = (columns, colId) => {
  if (!Array.isArray(columns) || !colId) return null
  const index = columnIDs(columns).indexOf(colId)
  return index === -1 ? null : columns[index]
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}($|[T ]\d{2}:\d{2})/

// What a column's values are: 'date' (ISO date strings or Dates), 'number',
// 'text', or null when they are mixed, empty or something else (booleans).
export const inferSortKind = values => {
  const sample = (values || []).filter(v => v !== null && v !== undefined && v !== '').slice(0, 50)
  if (sample.length === 0) return null
  if (sample.every(v => typeof v === 'number')) return 'number'
  if (sample.every(v => v instanceof Date || (typeof v === 'string' && ISO_DATE.test(v)))) return 'date'
  if (sample.every(v => typeof v === 'string')) return 'text'
  return null
}

const sortKindOf = (column, rows) => {
  if (!column) return null
  const context = column.context || {}
  if (context.sortKind) return context.sortKind
  if (column.comparator) return null
  if (Array.isArray(rows) && rows.length > 0 && column.field) {
    const inferred = inferSortKind(rows.map(row => (row ? row[column.field] : undefined)))
    if (inferred) return inferred
  }
  if (context.sortKindFallback) return context.sortKindFallback
  // No rows to go by (an empty or infinite grid): a camelCase "...At" field
  // (createdAt, expiresAt) holds a time by this codebase's convention.
  return column.field && /[a-z0-9]At$/.test(column.field) ? 'date' : null
}

export const sortChipText = ({ columns, rows, sortCol, sortDir }) => {
  const column = findSortColumn(columns, sortCol)
  const header = (column && column.headerName) || humanizeField(column ? column.field : sortCol)
  const direction = String(sortDir || '').toLowerCase() === 'desc' ? 'desc' : 'asc'
  const words = (DIRECTION_WORDS[sortKindOf(column, rows)] || NEUTRAL_WORDS)[direction]
  return `Sorted by ${header}, ${words}`
}
